import { useEffect, useState } from "react";
import { api } from "../../api/apiClient";
import { useT } from "../../i18n/LanguageProvider";
import type { PlainKey } from "../../i18n/types";
import { shopModeApi } from "../../api/shopMode";
import type { Task } from "../../api/types";

interface TaskImagesProps {
  task: Task;
  mode?: "crm" | "floor";
}

type LoadedTaskImage = {
  slot: "reference" | "completion";
  url: string;
};

/**
 * G-85 S4 — the caption is DERIVED AT RENDER, and the effect below must stay
 * free of `t`.
 *
 * The first extraction stored the translated label in state alongside the
 * blob URL. Two things were wrong with that. Switching language would not
 * have relabelled an image already on screen, because the words were frozen
 * when it was fetched. And `t` in the effect's dependency list would have
 * re-downloaded both blobs on every switch, revoking and re-creating the
 * object URLs as it went — eslint caught the dependency, which is what led
 * back to the first problem.
 */
function captionKey(slot: LoadedTaskImage["slot"]): PlainKey {
  return slot === "reference" ? "tasks.referencePhoto" : "tasks.completionPhoto";
}

export function TaskImages({ task, mode = "crm" }: TaskImagesProps) {
  const { t } = useT();
  const [images, setImages] = useState<LoadedTaskImage[]>([]);

  useEffect(() => {
    let cancelled = false;
    const urls: string[] = [];

    async function load() {
      const next: LoadedTaskImage[] = [];
      const fetcher =
        mode === "floor"
          ? shopModeApi.floorTaskImageBlobUrl
          : api.fetchTaskImageBlobUrl;

      if (task.hasReferenceImage) {
        const url = await fetcher(task.id, "reference");
        if (url) {
          urls.push(url);
          next.push({ slot: "reference", url });
        }
      }
      if (task.hasCompletionImage) {
        const url = await fetcher(task.id, "completion");
        if (url) {
          urls.push(url);
          next.push({ slot: "completion", url });
        }
      }
      if (!cancelled) setImages(next);
    }

    if (task.hasReferenceImage || task.hasCompletionImage) {
      void load();
    } else {
      setImages([]);
    }

    return () => {
      cancelled = true;
      for (const url of urls) URL.revokeObjectURL(url);
    };
  }, [mode, task.hasCompletionImage, task.hasReferenceImage, task.id]);

  if (images.length === 0) return null;

  return (
    <div className={`task-images${mode === "floor" ? " task-images--floor" : ""}`}>
      {images.map((image) => (
        <figure key={image.slot} className="task-image-figure">
          {mode === "floor" ? (
            <figcaption className="muted task-image-caption">{t(captionKey(image.slot))}</figcaption>
          ) : null}
          <img
            src={image.url}
            alt={t(captionKey(image.slot))}
            className="task-image"
          />
          {mode !== "floor" ? (
            <figcaption className="muted task-image-caption">{t(captionKey(image.slot))}</figcaption>
          ) : null}
        </figure>
      ))}
    </div>
  );
}
